import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Eye, EyeOff, Info } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const PASSWORD_RULES = [
  { label: "6–15 characters", test: (v) => v.length >= 6 && v.length <= 15 },
  { label: "At least one uppercase (A-Z)", test: (v) => /[A-Z]/.test(v) },
  { label: "At least one lowercase (a-z)", test: (v) => /[a-z]/.test(v) },
  { label: "At least one number (0-9)", test: (v) => /[0-9]/.test(v) },
  { label: "At least one special character (!@#$...)", test: (v) => /[^A-Za-z0-9]/.test(v) },
  { label: "No spaces", test: (v) => !/\s/.test(v) }
];

function getStrength(password) {
  const passed = PASSWORD_RULES.filter((r) => r.test(password)).length;
  if (passed <= 1) return { label: "Very weak", color: "#ef4444" };
  if (passed === 2) return { label: "Weak", color: "#f97316" };
  if (passed === 3) return { label: "Fair", color: "#eab308" };
  if (passed === 4) return { label: "Strong", color: "#00d4ff" };
  return { label: "Very strong", color: "#00ff88" };
}

function SignUpPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const strength = getStrength(formData.password);

  const [showPasswordInfo, setShowPasswordInfo] = useState(false);

  const [pasteWarning, setPasteWarning] = useState("");

  const validateField = (name, value) => {
    let msg = "";

    if (name === "name") {
      if (!value.trim()) msg = "Name required";
      else if (value.length < 2) msg = "Too short";
    }

    if (name === "email") {
      if (!value) msg = "Email required";
      else if (!EMAIL_RE.test(value)) msg = "Invalid email";
    }

    if (name === "password") {
      const passed = PASSWORD_RULES.filter((r) =>
        r.test(value)
      ).length;

      if (!value) msg = "Password required";
      else if (passed < 4) msg = "Password is too weak";
    }

    if (name === "confirmPassword") {
      if (!value) msg = "Confirm your password";
      else if (value !== formData.password)
        msg = "Passwords do not match";
    }

    setErrors((p) => ({ ...p, [name]: msg }));
    return msg;
  };

  const handleChange = (name, value) => {
    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((p) => ({ ...p, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nameErr = validateField("name", formData.name);
    const emailErr = validateField("email", formData.email);
    const passErr = validateField("password", formData.password);
    const confirmErr = validateField("confirmPassword", formData.confirmPassword);
    const phoneErr = validateField("phone", formData.phone);

    if (nameErr || emailErr || passErr || confirmErr || phoneErr) return;

    setLoading(true);

    try {
      const user = await signup(
        formData.name,
        formData.email,
        formData.phone,
        formData.password
      );

      if (user) {
        navigate("/signin", {
          state: { message: "Account created successfully. Please sign in." }
        });
      }

    } catch (err) {
      console.error(err);
      setApiError(err.message || "Signup failed");

      if (err.code === "auth/email-already-in-use") {
        setApiError("This email is already registered.");
      } else if (err.code === "auth/invalid-email") {
        setApiError("Invalid email address.");
      } else if (err.code === "auth/weak-password") {
        setApiError("Password should be at least 6 characters.");
      } else if (err.code === "permission-denied") {
        setApiError("Database permission denied. Check Firestore rules.");
      } else {
        setApiError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h1 style={styles.title}>Create Account</h1>
        <p style={styles.subtitle}>Join Carolina Sports Club</p>
        {
          apiError && (
            <p style={styles.error}>{apiError}</p>
          )
        }
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* NAME */}
          <div style={styles.inputGroup}>
            <input
              placeholder="Full Name *"
              value={formData.name}
              onChange={(e) =>
                handleChange("name", e.target.value.replace(/[^a-zA-Z\s]/g, ""))
              }
              style={styles.input}
            />
            {errors.name && <p style={styles.error}>{errors.name}</p>}
          </div>

          {/* EMAIL */}
          <div style={styles.inputGroup}>
            <input
              placeholder="Email *"
              value={formData.email}
              onChange={(e) =>
                handleChange("email", e.target.value)
              }
              style={styles.input}
            />
            {errors.email && <p style={styles.error}>{errors.email}</p>}
          </div>

          {/* PHONE */}
          <div style={styles.inputGroup}>
            <input
              placeholder="Phone (10 digits)"
              value={formData.phone}
              onChange={(e) =>
                handleChange("phone", e.target.value)
              }
              style={styles.input}
            />
            {errors.phone && <p style={styles.error}>{errors.phone}</p>}
          </div>

          {/* PASSWORD */}
          <div style={styles.inputGroup}>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password *"
                value={formData.password}
                onChange={(e) =>
                  handleChange("password", e.target.value.replace(/\s/g, ""))
                }
                style={styles.passwordInput}
              />

              {/* ICON GROUP */}
              <div style={styles.iconGroup}>
                <span
                  style={styles.icon}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </span>

                <span
                  style={styles.icon}
                  onClick={() => setShowPasswordInfo(!showPasswordInfo)}
                >
                  <Info size={16} />
                </span>
              </div>
            </div>
            {showPasswordInfo && (
              <div style={styles.infoBox}>
                <p style={styles.infoTitle}>Password must include:</p>
                <ul style={styles.infoList}>
                  {PASSWORD_RULES.map((rule) => (
                    <li key={rule.label} style={styles.infoItem}>
                      • {rule.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {formData.password && (
              <p style={{ ...styles.strength, color: strength.color }}>
                {strength.label}
              </p>
            )}

            {errors.password && (
              <p style={styles.error}>{errors.password}</p>
            )}
          </div>

          {/* CONFIRM PASSWORD */}
          <div style={styles.inputGroup}>
            <input
              type="password"
              placeholder="Confirm Password *"
              value={formData.confirmPassword}
              onChange={(e) =>
                handleChange(
                  "confirmPassword",
                  e.target.value
                )
              }
              onPaste={(e) => {
                e.preventDefault();
                setPasteWarning("Pasting is not allowed. Please type your password.");

                // auto-hide after 2 seconds
                setTimeout(() => setPasteWarning(""), 2000);
              }}
              style={styles.input}
            />
            {pasteWarning && (
              <p style={styles.warning}>{pasteWarning}</p>
            )}

            {formData.confirmPassword &&
              formData.confirmPassword === formData.password && (
                <p
                  style={{
                    fontSize: "12px",
                    marginTop: "6px",
                    color: "#00f5a0",
                  }}
                >
                  Passwords match
                </p>
              )}
            {errors.confirmPassword && (
              <p style={styles.error}>{errors.confirmPassword}</p>
            )}
          </div>

          {/* BUTTON */}
          <button style={styles.button}>
            {loading ? "Creating..." : "Create Account"}
          </button>
        </form>

        <p style={styles.footer}>
          Already have an account?{" "}
          <Link to="/signin" style={styles.link}>
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}

/* ── STYLES ── */
const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background:
      "linear-gradient(111.1deg, rgb(0,40,70) -4.8%, rgb(255,229,115) 82.7%, rgb(255,203,15) 97.2%)",
  },

  card: {
    width: "100%",
    maxWidth: "400px",
    padding: "30px",
    borderRadius: "20px",
    background: "rgba(0,40,70,0.85)",
    border: "1px solid rgba(0,255,150,0.3)",
    backdropFilter: "blur(10px)",
    color: "#fff",
  },

  title: {
    fontSize: "28px",
    fontWeight: "bold",
    textAlign: "center",
  },

  subtitle: {
    textAlign: "center",
    color: "#ccc",
    marginBottom: "20px",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },

  inputGroup: {
    display: "flex",
    flexDirection: "column",
  },

  input: {
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid rgba(0,255,150,0.3)",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    outline: "none",
  },

  passwordWrapper: {
    position: "relative",
    width: "100%"
  },

  passwordInput: {
    width: "100%",
    padding: "12px 40px 12px 12px",
    paddingRight: "40px",
    borderRadius: "10px",
    border: "1px solid rgba(0,255,150,0.3)",
    background: "rgba(255,255,255,0.05)",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box",
  },

  iconGroup: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    display: "flex",
    gap: "10px", // space between 👁️ and ⓘ
  },

  icon: {
    cursor: "pointer",
    fontSize: "16px",
    color: "#aaa",
  },

  label: {
    fontSize: "14px",
    color: "#d1d5db",
  },

  required: {
    color: "#ef4444",
  },

  infoBox: {
    marginTop: "10px",
    padding: "10px",
    borderRadius: "10px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(0,255,150,0.2)",
  },

  infoTitle: {
    fontSize: "12px",
    marginBottom: "6px",
    color: "#00f5a0",
  },

  infoList: {
    paddingLeft: "16px",
    margin: 0,
  },

  infoItem: {
    fontSize: "12px",
    color: "#ccc",
    marginBottom: "4px",
  },

  error: {
    color: "#ff6b6b",
    fontSize: "12px",
    marginTop: "4px",
  },

  strength: {
    fontSize: "12px",
    marginTop: "5px",
  },

  button: {
    marginTop: "10px",
    padding: "12px",
    borderRadius: "25px",
    border: "none",
    fontWeight: "bold",
    background: "linear-gradient(90deg, #00f5a0, #00d9f5)",
    cursor: "pointer",
  },

  footer: {
    textAlign: "center",
    marginTop: "20px",
    fontSize: "14px",
  },

  link: {
    color: "#00f5a0",
    textDecoration: "none",
    fontWeight: "bold",
  },

  warning: {
    fontSize: "12px",
    color: "#f59e0b", // amber/yellow (warning, not error)
    marginTop: "6px",
  },
};

export default SignUpPage;